"use client";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React, { useMemo } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { WORKFLOW_PERMISSIONS } from "@/constants/modules.permissions";
import { defaultPageNumber, defaultPageSize } from "@/constants/pagination";
import {
  WORKFLOW_TYPE_SELECT_OPTIONS,
  buildWorkflowTypeLabelResolver,
} from "@/helpers/workflow-type-display";
import useGetAllWorkflowTypes from "@/hooks/requests/wokflow/use-get-all-workflow-types";
import useGetWorkflows from "@/hooks/requests/wokflow/use-get-workflow";
import useGetWorkflowsBackoffice from "@/hooks/requests/wokflow/use-get-workflows-backoffice";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";

import { WorkflowActivationDialog } from "./components/WorkflowDialog";
import WorkflowFiltersForm from "./components/WorkflowFiltersForm";
import { WorkflowTable } from "./components/WorkflowTable/index";
import { initialFilterValues } from "./constants";

type WorkflowsProps = {
  messages: WorkflowMessages;
};

export default function WorkflowPage({ messages }: WorkflowsProps) {
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const filter = useFilters<IWorkflow>({ initialValue: initialFilterValues });
  const filters = filter.filters;

  const { data: workflows, isLoading, error } = useGetWorkflows({ filters, pageNumber, pageSize });
  const { isVisible, handleOpen, handleClose } = useDialog<IWorkflow>();
  const hasNoPermissionToCreate = useHasNoPermission(WORKFLOW_PERMISSIONS.CREATE);

  const { data: workflowsBackoffice } = useGetWorkflowsBackoffice({
    pageNumber: defaultPageNumber,
    pageSize: defaultPageSize,
  });

  const { data: workflowTypesData } = useGetAllWorkflowTypes();
  const workflowTypeItems = workflowTypesData?.items;

  const resolveWorkflowTypeLabel = useMemo(
    () => buildWorkflowTypeLabelResolver(workflowTypeItems, messages),
    [workflowTypeItems, messages]
  );

  const agentTypeOptions = useMemo(
    () => WORKFLOW_TYPE_SELECT_OPTIONS(workflowTypeItems),
    [workflowTypeItems]
  );

  const hasWorkflows = !!workflows && workflows.items.length > 0;

  const shouldLoadWorkflowsTable = hasWorkflows && !isLoading && !error;
  const shouldLoadError = !hasWorkflows && !isLoading && !!error;
  const shouldLoadNoWorkflowsFound = !!workflows && !isLoading && !error && !workflows.items.length;

  const {
    workflowNotFoundAlt,
    workflowNotFoundDescription,
    workflowNotFoundError,
    workflowErrorToLoad,
    activeButton,
    workflowTableTitle,
    pageTitle,
  } = messages;

  return (
    <PageLayout messages={messages} title={pageTitle}>
      <PageContainer>
        <Formik initialValues={filters} validateOnChange={false} onSubmit={filter.changeFilters}>
          <WorkflowFiltersForm
            messages={messages}
            temporaryFilters={filter.temporaryFilters}
            deleteTemporaryFilter={filter.deleteTemporaryFilter}
            agentTypeOptions={agentTypeOptions}
            resolveWorkflowTypeLabel={resolveWorkflowTypeLabel}
          />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {workflowTableTitle}
                </MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  endIcon={<AddCircleOutlineIcon />}
                  onClick={() => handleOpen()}
                  disabled={hasNoPermissionToCreate}
                >
                  {activeButton}
                </MDButton>
              </MDBox>

              {!!shouldLoadWorkflowsTable && (
                <WorkflowTable
                  pagination={pagination}
                  data={workflows}
                  messages={messages}
                  resolveWorkflowTypeLabel={resolveWorkflowTypeLabel}
                />
              )}
              {!!shouldLoadError && (
                <AlertBlock
                  title={workflowErrorToLoad}
                  description={error.message}
                  alt={workflowNotFoundAlt}
                />
              )}
              {!!shouldLoadNoWorkflowsFound && (
                <AlertBlock
                  title={workflowNotFoundError}
                  description={workflowNotFoundDescription}
                  alt={workflowNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>

        <WorkflowActivationDialog
          isOpen={isVisible}
          onClose={handleClose}
          messages={messages}
          workflows={workflowsBackoffice?.items}
          isLoading={isLoading}
        />
      </PageContainer>
    </PageLayout>
  );
}
